<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background mit dynamischen Farben -->
    <div class="fixed inset-0 -z-10" :style="{ '--accent-color': accentColor }">
      <!-- Gradient Background -->
      <div class="absolute inset-0" :style="{ background: isDark ? `linear-gradient(to bottom right, ${accentColor}0D, ${accentColor}08, #111827, ${accentColor}0A)` : `linear-gradient(to bottom right, ${accentColor}0D, ${accentColor}08, white, ${accentColor}0A)` }"></div>
      
      <!-- Animierte Blobs -->
      <div class="absolute top-0 left-0 w-[500px] h-[500px] rounded-[60%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-60 animate-blob" :style="{ backgroundColor: `${accentColor}14` }"></div>
      
      <div class="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-[50%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-50 animate-blob animation-delay-2000" :style="{ backgroundColor: `${accentColor}19` }"></div>
      
      <div class="absolute bottom-0 left-1/4 w-[550px] h-[550px] rounded-[55%] mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-45 animate-blob animation-delay-4000" :style="{ backgroundColor: `${accentColor}1F` }"></div>
      
      <div class="absolute top-1/2 right-1/3 w-[400px] h-[400px] rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-35 animate-blob-organic animation-delay-1500" :style="{ backgroundColor: `${accentColor}14` }"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] opacity-60"></div>
      
      <!-- Subtile Wellen-Effekte -->
      <div class="absolute bottom-0 left-0 right-0 h-64" :style="{ background: `linear-gradient(to top, ${accentColor}33, transparent)` }"></div>
    </div>

    <Navigation />

    <!-- Hero Section -->
    <section class="pt-36 pb-16 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col lg:flex-row items-center gap-12 mb-12">
          <!-- Text Content -->
          <div class="flex-1 text-center lg:text-left">
            <div v-if="logoUrl" class="flex justify-center lg:justify-start mb-8 reveal">
              <img 
                :key="`bey-hub-logo-${isDark}-${logoUrl}`"
                :src="logoUrl"
                alt="BEY-Hub Logo"
                class="h-20 md:h-24 object-contain transition-opacity duration-300"
              />
            </div>
            <div class="inline-block mb-6 reveal">
              <div class="px-5 py-2.5 bg-white dark:bg-gray-800/80 backdrop-blur-md rounded-full border-2 shadow-md" :style="{ borderColor: `${accentColor}4D` }">
                <span class="text-sm font-medium text-gray-800 dark:text-white">🎮 Community Platform</span>
              </div>
            </div>
            <h1 class="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6 reveal delay-100 leading-tight">
              BEY-Hub.de
            </h1>
            <div class="mt-4 h-2 w-32 mx-auto lg:mx-0 rounded-full reveal delay-200" :style="{ backgroundColor: accentColor }"></div>
            <p class="mt-6 text-xl md:text-2xl font-medium text-gray-700 dark:text-white reveal delay-300">
              Fan-Webseite für Beyblade X mit Community-Funktionen
            </p>
            <div class="mt-8 text-lg text-gray-600 dark:text-gray-400 leading-relaxed reveal delay-400 space-y-4">
              <p>
                <span class="font-bold">BEY-Hub.de</span> ist eine umfassende Fan-Webseite für Beyblade X, die ich komplett ins Leben gerufen und verwaltet habe. Die Plattform bietet Community-Funktionen, Teile-Verwaltung und Build-Generierung für alle Beyblade X Fans.
              </p>
              <p>
                Die Software erlaubt es Nutzern ihre <span class="font-bold">Teile zu verwalten</span>, <span class="font-bold">Beyblade Builds zu generieren</span> und zukünftig mit ihren User-Profilen an <span class="font-bold">lokalen Turnieren teilzunehmen</span>, die über die Webseite organisiert und verwaltet werden.
              </p>
              <p>
                Geplant für die Verwendung bei <span class="font-bold">zukünftigen Turnieren in ganz Deutschland</span>, bietet BEY-Hub.de eine zentrale Plattform für die wachsende Beyblade X Community.
              </p>
              <p class="text-base font-semibold">
                Entwickelt mit modernen Web-Technologien für maximale Performance und Community-Erlebnis.
              </p>
              <div v-if="liveUrl" class="mt-6">
                <a 
                  :href="liveUrl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                  :style="{ 
                    backgroundColor: accentColor, 
                    color: 'white'
                  }"
                >
                  <GlobeAltIcon class="w-5 h-5" />
                  <span>Zur Webseite</span>
                </a>
              </div>
            </div>
          </div>

          <!-- Hero Screenshot -->
          <div class="flex-1 w-full max-w-2xl reveal delay-300">
            <div class="relative group">
              <div class="absolute -inset-4 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition duration-500" :style="{ background: `linear-gradient(to right, ${accentColor}33, ${accentColor}33)` }"></div>
              <div class="relative rounded-3xl overflow-hidden shadow-2xl border-2 cursor-pointer" :style="{ backgroundColor: isDark ? '#1f2937' : 'white', borderColor: isDark ? 'rgba(55, 65, 81, 0.5)' : '#e5e7eb' }" @click="openLightbox(heroImage)">
                <img 
                  :src="heroImage" 
                  alt="BEY-Hub.de Webseite"
                  class="w-full h-auto object-cover hover:scale-105 transition duration-700"
                />
                <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div class="bg-white/20 backdrop-blur-md p-3 rounded-full">
                    <MagnifyingGlassPlusIcon class="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tech Stack -->
        <div class="mb-12 reveal delay-500">
          <div class="flex flex-wrap justify-center gap-2 md:gap-3">
            <span
              v-for="tech in displayedTechnologies"
              :key="tech"
              class="px-2 py-1 md:px-4 md:py-2 bg-white dark:bg-gray-800 backdrop-blur-sm rounded-full md:rounded-full text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-md md:shadow-lg border-2 dark:border-gray-600/50 hover:shadow-xl transition-all duration-300"
              :style="{ 
                borderColor: isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}80`,
              }"
              @mouseenter="(e) => handleMouseEnter(e, accentColor)"
              @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}80`)"
            >
              {{ tech }}
            </span>
          </div>
          <!-- "Mehr anzeigen" Button nur auf mobil -->
          <button
            v-if="!showAllTechs && project.technologies.length > 8"
            @click="showAllTechs = true"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold hover:underline transition-colors"
            :style="{ color: accentColor }"
          >
            + {{ project.technologies.length - 8 }} weitere anzeigen
          </button>
          <button
            v-else-if="showAllTechs"
            @click="showAllTechs = false"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold hover:underline transition-colors"
            :style="{ color: accentColor }"
          >
            Weniger anzeigen
          </button>
        </div>
      </div>
    </section>

    <!-- Project Stats / Highlights Grid -->
    <section class="pb-32 md:pb-40 px-4 sm:px-6 lg:px-8 relative">
      <div class="max-w-7xl mx-auto mb-12 md:mb-16">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <div v-for="(stat, i) in stats" :key="i" class="bg-white dark:bg-gray-800/80 backdrop-blur-md p-4 md:p-6 rounded-2xl md:rounded-3xl border-2 dark:border-gray-600/50 shadow-lg text-center reveal hover:shadow-2xl hover:scale-105 transition-all duration-300" :style="{ transitionDelay: `${i * 100}ms`, borderColor: isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}99` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}99`)">
            <component :is="stat.icon" class="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-4" :style="{ color: accentColor }" />
            <div class="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">{{ stat.value }}</div>
            <div class="text-xs md:text-sm text-gray-600 dark:text-gray-300 uppercase tracking-wider font-semibold leading-tight">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20 pb-32 md:pb-40 px-4 sm:px-6 lg:px-8 dark:bg-black/10 backdrop-blur-sm relative" :style="{ backgroundColor: isDark ? 'rgba(0, 0, 0, 0.1)' : `${accentColor}0A` }">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16 reveal">
          <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Features & Funktionalität
          </h2>
          <div class="mt-4 h-2 w-24 mx-auto rounded-full" :style="{ backgroundColor: accentColor }"></div>
          <p class="mt-6 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Umfassende Community-Plattform für Beyblade X Fans
          </p>
        </div>

        <div class="space-y-16 md:space-y-24">
          <div
            v-for="(feature, index) in sortedFeatures"
            :key="feature.id"
            class="feature-item reveal"
            :style="{ transitionDelay: `${index * 100}ms` }"
          >
            <!-- Modern Card Layout: Mobile = Single Container, Desktop = Grid -->
            <div 
              class="relative group flex flex-col md:grid md:grid-cols-2 md:gap-8"
            >
                <!-- Mobile: Gemeinsamer Container für Text und Media -->
              <div class="md:hidden relative backdrop-blur-md rounded-3xl shadow-2xl border-2 transition-all duration-500 overflow-hidden" :style="{ borderColor: isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`, background: isDark ? `linear-gradient(to bottom right, #1f2937, #111827, ${accentColor}0A)` : `linear-gradient(to bottom right, white, white, ${accentColor}0A)` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`)">
                <!-- Decorative Background Element -->
                <div class="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" :style="{ background: `radial-gradient(circle, ${accentColor}0D, transparent)` }"></div>
                
                <!-- Text Content (oben auf mobil) -->
                <div class="relative p-6 pb-4">
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="p-2.5 rounded-2xl border group-hover:scale-110 transition-transform duration-300" :style="{ background: `linear-gradient(to bottom right, ${accentColor}1A, ${accentColor}19)`, borderColor: `${accentColor}33` }">
                        <component :is="feature.icon" class="w-6 h-6" :style="{ color: accentColor }" />
                      </div>
                      <div class="px-3 py-1 rounded-full text-xs font-bold border" :style="{ background: isDark ? `${accentColor}33` : `${accentColor}19`, color: accentColor, borderColor: `${accentColor}33` }">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 transition-colors duration-300 leading-tight" :style="{ '--accent': accentColor }" @mouseenter="(e) => handleMouseEnterColor(e, accentColor)" @mouseleave="handleMouseLeaveColor">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    {{ feature.description }}
                  </p>

                    <!-- Tags -->
                    <div v-if="feature.tags" class="relative flex flex-wrap gap-2">
                      <span 
                        v-for="tag in feature.tags" 
                        :key="tag" 
                        class="text-xs font-semibold px-3 py-1 max-w-[45%] dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 dark:border-gray-600/50 transition-colors duration-300"
                        :style="{ backgroundColor: isDark ? 'rgba(55, 65, 81, 0.6)' : `${accentColor}14`, borderColor: `${accentColor}80` }"
                        @mouseenter="(e) => handleMouseEnter(e, accentColor)"
                        @mouseleave="(e) => handleMouseLeave(e, `${accentColor}80`)"
                      >
                        {{ tag }}
                      </span>
                    </div>
                </div>

                <!-- Media Container (unten auf mobil) -->
                <div v-if="feature.type !== 'text-only'" class="relative w-full group/media px-4 pb-4">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-4 rounded-[2rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse" :style="{ background: `linear-gradient(to right, ${accentColor}33, ${accentColor}33, ${accentColor}33)` }"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full min-h-[300px] rounded-2xl overflow-hidden shadow-xl border-2 transition-all duration-500 flex items-center justify-center p-3" :style="{ borderColor: isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`, background: isDark ? `linear-gradient(to bottom right, #1f2937, ${accentColor}0A, #111827)` : `linear-gradient(to bottom right, white, ${accentColor}0A, white)` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`)">
                    <!-- Single Image -->
                    <div v-if="feature.type === 'image' && feature.content" :class="['w-full relative cursor-pointer overflow-hidden flex items-center justify-center rounded-xl', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-[400px]' : 'h-full min-h-[300px]']" :style="{ backgroundColor: isDark ? '#111827' : '#f9fafb' }" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        :class="['rounded-lg group-hover/media:scale-105 transition-transform duration-700', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-full w-auto object-contain' : 'max-w-full max-h-full object-contain']"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-4 h-4" :style="{ color: accentColor }" />
                          <span class="text-xs font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Hover Glow Effect -->
                <div class="absolute inset-0 rounded-3xl transition-all duration-500 pointer-events-none" :style="{ background: 'transparent' }" @mouseenter="(e) => handleMouseEnterGradient(e, accentColor)" @mouseleave="handleMouseLeaveGradient"></div>
              </div>

              <!-- Desktop: Feature Content Card -->
              <div 
                class="hidden md:block relative"
                :class="index % 2 === 1 && feature.type !== 'text-only' ? 'md:order-2' : feature.type === 'text-only' ? 'md:col-span-2' : ''"
              >
                <div class="relative h-full backdrop-blur-md rounded-3xl p-10 shadow-2xl border-2 transition-all duration-500 overflow-hidden" :class="feature.type === 'text-only' ? 'min-h-[400px]' : ''" :style="{ borderColor: isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`, background: isDark ? `linear-gradient(to bottom right, #1f2937, #111827, ${accentColor}0A)` : `linear-gradient(to bottom right, white, white, ${accentColor}0A)` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`)">
                  <!-- Decorative Background Element -->
                  <div class="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" :style="{ background: `radial-gradient(circle, ${accentColor}0D, transparent)` }"></div>
                  
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-6">
                    <div class="flex items-center gap-4">
                      <div class="p-3 rounded-2xl border group-hover:scale-110 transition-transform duration-300" :style="{ background: `linear-gradient(to bottom right, ${accentColor}1A, ${accentColor}19)`, borderColor: `${accentColor}33` }">
                        <component :is="feature.icon" class="w-7 h-7" :style="{ color: accentColor }" />
                      </div>
                      <div class="px-4 py-1.5 rounded-full text-xs font-bold border" :style="{ background: isDark ? `${accentColor}33` : `${accentColor}19`, color: accentColor, borderColor: `${accentColor}33` }">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6 transition-colors duration-300 leading-tight" @mouseenter="(e) => handleMouseEnterColor(e, accentColor)" @mouseleave="handleMouseLeaveColor">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
                    {{ feature.description }}
                  </p>

                  <!-- Tags -->
                  <div v-if="feature.tags" class="relative flex flex-wrap gap-2.5">
                    <span 
                      v-for="tag in feature.tags" 
                      :key="tag" 
                      class="text-xs font-semibold px-4 py-1.5 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 dark:border-gray-600/50 transition-colors duration-300"
                      :style="{ backgroundColor: isDark ? 'rgba(55, 65, 81, 0.6)' : `${accentColor}14`, borderColor: `${accentColor}80` }"
                      @mouseenter="(e) => handleMouseEnter(e, accentColor)"
                      @mouseleave="(e) => handleMouseLeave(e, `${accentColor}80`)"
                    >
                      {{ tag }}
                    </span>
                  </div>

                  <!-- Hover Glow Effect -->
                  <div class="absolute inset-0 rounded-3xl transition-all duration-500 pointer-events-none" :style="{ background: 'transparent' }" @mouseenter="(e) => handleMouseEnterGradient(e, accentColor)" @mouseleave="handleMouseLeaveGradient"></div>
                </div>
              </div>

              <!-- Desktop: Feature Media Container -->
              <div 
                v-if="feature.type !== 'text-only'"
                class="hidden md:block relative"
                :class="index % 2 === 1 ? 'md:order-1' : ''"
              >
                <div class="relative w-full h-full min-h-[500px] flex items-center justify-center group/media">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-6 rounded-[2.5rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse" :style="{ background: `linear-gradient(to right, ${accentColor}33, ${accentColor}33, ${accentColor}33)` }"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl border-2 transition-all duration-500 flex items-center justify-center p-6" :style="{ borderColor: isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`, background: isDark ? `linear-gradient(to bottom right, #1f2937, ${accentColor}0A, #111827)` : `linear-gradient(to bottom right, white, ${accentColor}0A, white)` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(55, 65, 81, 0.5)' : `${accentColor}99`)">
                    <!-- Single Image -->
                    <div v-if="feature.type === 'image' && feature.content" :class="['w-full relative cursor-pointer overflow-hidden flex items-center justify-center rounded-2xl', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-[500px]' : 'h-full']" :style="{ backgroundColor: isDark ? '#111827' : '#f9fafb' }" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        :class="['rounded-xl group-hover/media:scale-105 transition-transform duration-700', 'mobileImageStyle' in feature && feature.mobileImageStyle ? 'h-full w-auto object-contain' : 'max-w-full max-h-full object-contain']"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-5 h-5" :style="{ color: accentColor }" />
                          <span class="text-sm font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Project Info Grid -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 dark:bg-black/10 backdrop-blur-sm" :style="{ backgroundColor: isDark ? 'rgba(0, 0, 0, 0.1)' : `${accentColor}0A` }">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 dark:border-gray-600/50 shadow-lg text-center hover:shadow-2xl transition-all duration-300 reveal" :style="{ borderColor: isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}99` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}99`)">
            <h4 class="font-bold mb-4 uppercase tracking-wider text-sm" :style="{ color: accentColor }">Plattform</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Web-Plattform</p>
          </div>
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 shadow-lg text-center hover:shadow-2xl transition-all duration-300 reveal delay-100" :style="{ borderColor: isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}80` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}80`)">
            <h4 class="font-bold mb-4 uppercase tracking-wider text-sm" :style="{ color: accentColor }">Fokus</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Community & Turniere</p>
          </div>
          <div class="bg-white dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 shadow-lg text-center hover:shadow-2xl transition-all duration-300 reveal delay-200" :style="{ borderColor: isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}80` }" @mouseenter="(e) => handleMouseEnter(e, accentColor)" @mouseleave="(e) => handleMouseLeave(e, isDark ? 'rgba(75, 85, 99, 0.5)' : `${accentColor}80`)">
            <h4 class="font-bold mb-4 uppercase tracking-wider text-sm" :style="{ color: accentColor }">Zielgruppe</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Beyblade X Community</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Back to Projekte -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 dark:bg-black/10 backdrop-blur-sm" :style="{ backgroundColor: isDark ? 'rgba(0, 0, 0, 0.1)' : `${accentColor}0A` }">
      <div class="max-w-7xl mx-auto text-center reveal">
        <router-link
          to="/portfolio"
          class="inline-flex items-center space-x-3 px-10 py-4 bg-white dark:bg-gray-800 font-bold rounded-2xl shadow-xl dark:hover:bg-gray-700 transition-all duration-300 hover:scale-105 border-2"
          :style="{ 
            color: accentColor,
            borderColor: `${accentColor}4D`,
          }"
          @mouseenter="(e) => { handleMouseEnterBackground(e, `${accentColor}0D`); handleMouseEnter(e, `${accentColor}80`) }"
          @mouseleave="(e) => { handleMouseLeaveBackground(e, isDark ? 'rgb(31, 41, 55)' : 'white'); handleMouseLeave(e, `${accentColor}4D`) }"
        >
          <ArrowLeftIcon class="w-5 h-5" />
          <span>Zurück zur Übersicht</span>
        </router-link>
      </div>
    </section>

    <StandardFooter :accent-color="accentColor" />

    <!-- Lightbox Modal -->
    <Transition name="fade">
      <div v-if="lightboxUrl" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" @click="closeLightbox">
        <button class="absolute top-6 right-6 text-white transition-colors p-2" :style="{ '--hover-color': accentColor }" @mouseenter="(e) => handleMouseEnterColor(e, accentColor)" @mouseleave="(e) => { const target = e.currentTarget as HTMLElement; if (target) target.style.color = 'white' }" @click="closeLightbox">
          <XMarkIcon class="w-8 h-8" />
        </button>
        <img :src="lightboxUrl" alt="Lightbox Image" class="max-w-full max-h-full object-contain rounded-lg shadow-2xl" @click.stop />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount, onUnmounted, watch } from 'vue'
import Navigation from '../../components/Navigation.vue'
import StandardFooter from '../../components/StandardFooter.vue'
import { useTheme } from '../../composables/useTheme'
import { getPortfolioProjectByPageUrl } from '../../firebase/portfolio'
import { logger } from '../../utils/logger'
import { 
  ArrowLeftIcon, 
  UserGroupIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  TrophyIcon,
  GlobeAltIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import type { Component } from 'vue'

const { isDark } = useTheme()

// Projekt-Daten aus Firebase
const projectData = ref<{
  logoLight?: string
  logoDark?: string
  accentColor?: string
  liveUrl?: string
} | null>(null)

// Logo aus Firebase laden
const logoUrl = ref<string>('')
const heroImage = ref<string>('/projects/bey-hub/hero.webp')
const accentColor = ref<string>('#2c6ae8') // Fallback: Blau
const liveUrl = ref<string>('')

// Hex zu RGB konvertieren
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

// Setze CSS-Variable für Accent-Farbe
const setAccentColor = (color: string) => {
  if (typeof document === 'undefined') return
  accentColor.value = color
  const rgb = hexToRgb(color)
  if (rgb) {
    document.documentElement.style.setProperty('--bey-hub-accent-r', rgb.r.toString())
    document.documentElement.style.setProperty('--bey-hub-accent-g', rgb.g.toString())
    document.documentElement.style.setProperty('--bey-hub-accent-b', rgb.b.toString())
    document.documentElement.style.setProperty('--bey-hub-accent', color)
    document.documentElement.style.setProperty('--project-accent', color)
  }
}

// Projekt-Daten laden
const loadProjectData = async () => {
  try {
    const project = await getPortfolioProjectByPageUrl('/projects/bey-hub')
    if (project) {
      projectData.value = project
      
      // Logo laden
      if (isDark.value && project.logoDark) {
        logoUrl.value = project.logoDark
      } else if (!isDark.value && project.logoLight) {
        logoUrl.value = project.logoLight
      } else {
        // Fallback zu lokalem Logo
        logoUrl.value = '/projects/bey-hub/logo.webp'
      }
      
      // Accent Color laden und setzen
      if (project.accentColor) {
        setAccentColor(project.accentColor)
      } else {
        // Fallback: Setze Standard-Farbe auch als generische Variable
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--project-accent', '#2c6ae8')
        }
      }
      
      // Live URL laden
      if (project.liveUrl) {
        liveUrl.value = project.liveUrl
      }
    } else {
      // Fallback zu lokalem Logo
      logoUrl.value = ''
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Projekt-Daten', error)
    // Fallback zu lokalem Logo
    logoUrl.value = ''
  }
}

// Logo aktualisieren, wenn Theme wechselt
const updateLogo = async () => {
  if (projectData.value) {
    if (isDark.value && projectData.value.logoDark) {
      logoUrl.value = projectData.value.logoDark
    } else if (!isDark.value && projectData.value.logoLight) {
      logoUrl.value = projectData.value.logoLight
    } else {
      logoUrl.value = ''
    }
  } else {
    logoUrl.value = ''
  }
}

// Watch für Theme-Änderungen
const stopWatch = watch(isDark, () => {
  updateLogo()
}, { immediate: false })

// Feature Type Definitions
interface BaseFeature {
  id: string
  title: string
  description: string
  tags?: string[]
  icon: Component
  order: number
}

interface ImageFeature extends BaseFeature {
  type: 'image'
  content: string
  mobileImageStyle?: boolean
}

interface TextOnlyFeature extends BaseFeature {
  type: 'text-only'
  content: string
}

type ProjectFeature = ImageFeature | TextOnlyFeature

const showAllTechs = ref(false)
const isMobile = ref(false)

const checkIfMobile = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

const displayedTechnologies = computed(() => {
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    return project.value.technologies
  }
  // Auf mobil: Wenn "Mehr anzeigen" geklickt wurde, alle anzeigen, sonst nur erste 8
  if (showAllTechs.value) {
    return project.value.technologies
  }
  return project.value.technologies.slice(0, 8)
})

const stats = [
  { icon: UserGroupIcon, value: 'Community', label: 'Plattform' },
  { icon: PuzzlePieceIcon, value: 'Teile', label: 'Verwaltung' },
  { icon: RocketLaunchIcon, value: 'Builds', label: 'Generator' },
  { icon: TrophyIcon, value: 'Turniere', label: 'Organisation' }
]

interface Project {
  technologies: string[]
  features: ProjectFeature[]
}

const project = ref<Project>({
  technologies: ['React', 'TypeScript', 'Firebase', 'Firestore', 'Firebase Auth', 'Community Features', 'Responsive Design', 'Modern UI'],
  features: [
    {
      id: 'feature-community',
      title: 'Community-Funktionen',
      description: 'BEY-Hub.de bietet umfassende Community-Funktionen für alle Beyblade X Fans. Nutzer können Profile erstellen, sich mit anderen Spielern vernetzen und die wachsende Community aktiv mitgestalten. Die Plattform dient als zentrale Anlaufstelle für alle Interessierten und ermöglicht den Austausch von Erfahrungen, Strategien und Builds.',
      type: 'text-only',
      content: '',
      tags: ['User-Profile', 'Community', 'Vernetzung'],
      icon: UserGroupIcon,
      order: 1
    },
    {
      id: 'feature-parts',
      title: 'Teile-Verwaltung',
      description: 'Ein umfassendes System zur Verwaltung aller Beyblade X Teile ermöglicht es Nutzern, ihre Sammlung zu organisieren und zu verwalten. Jedes Teil kann mit detaillierten Informationen versehen werden, was eine präzise Übersicht über die verfügbaren Komponenten schafft. Dies bildet die Grundlage für die Build-Generierung und strategische Planung.',
      type: 'image',
      content: '/projects/bey-hub/MeineSammlung.webp',
      tags: ['Teile-Datenbank', 'Sammlung', 'Organisation'],
      icon: PuzzlePieceIcon,
      order: 2
    },
    {
      id: 'feature-builds',
      title: 'Beyblade Build-Generator',
      description: 'Der Build-Generator ermöglicht es Nutzern, aus ihren verfügbaren Teilen optimale Beyblade-Konfigurationen zu erstellen. Das System unterstützt bei der Auswahl kompatibler Komponenten und bietet Vorschläge für verschiedene Spielstile und Strategien. Generierte Builds können gespeichert, geteilt und in der Community diskutiert werden.',
      type: 'image',
      content: '/projects/bey-hub/Beyblade Builder.webp',
      tags: ['Build-Generator', 'Strategie', 'Optimierung'],
      icon: RocketLaunchIcon,
      order: 3
    },
    {
      id: 'feature-tournaments',
      title: 'Turnier-Organisation & Verwaltung',
      description: 'Die Plattform bietet umfassende Funktionen zur Organisation und Verwaltung von lokalen Turnieren in ganz Deutschland. Veranstalter können Turniere anlegen, Teilnehmer verwalten und den gesamten Ablauf über die Webseite koordinieren. Nutzer können sich mit ihren Profilen für Turniere anmelden und ihre Teilnahme verwalten. Dies schafft eine zentrale Plattform für die wachsende Turnier-Szene.',
      type: 'image',
      content: '/projects/bey-hub/Tuniere.webp',
      tags: ['Turnier-Management', 'Anmeldungen', 'Organisation'],
      icon: TrophyIcon,
      order: 4
    },
    {
      id: 'feature-platform',
      title: 'Moderne Web-Plattform',
      description: 'BEY-Hub.de wurde mit modernen Web-Technologien entwickelt, um eine schnelle, responsive und benutzerfreundliche Erfahrung zu bieten. Die Plattform ist für alle Geräte optimiert und bietet eine intuitive Benutzeroberfläche, die sowohl für Einsteiger als auch für erfahrene Spieler geeignet ist. Regelmäßige Updates basierend auf Community-Feedback sorgen für kontinuierliche Verbesserungen.',
      type: 'text-only',
      content: '',
      tags: ['Modern UI', 'Responsive', 'Community-Driven'],
      icon: GlobeAltIcon,
      order: 5
    }
  ]
})

// Scroll Animation Logic
const initScrollReveal = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el))
}

const sortedFeatures = computed(() => {
  return [...project.value.features].sort((a, b) => a.order - b.order)
})

// Lightbox Logic
const lightboxUrl = ref<string | null>(null)
const openLightbox = (url: string) => {
  lightboxUrl.value = url
  document.body.style.overflow = 'hidden'
}
const closeLightbox = () => {
  lightboxUrl.value = null
  document.body.style.overflow = ''
}

// Helper-Funktionen für Event-Handler mit Type-Safety
const handleMouseEnter = (e: MouseEvent, color: string) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.borderColor = color
  }
}

const handleMouseLeave = (e: MouseEvent, defaultColor: string) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.borderColor = defaultColor
  }
}

const handleMouseEnterColor = (e: MouseEvent, color: string) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.color = color
  }
}

const handleMouseLeaveColor = (e: MouseEvent) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.color = ''
  }
}

const handleMouseEnterBackground = (e: MouseEvent, color: string) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.backgroundColor = color
  }
}

const handleMouseLeaveBackground = (e: MouseEvent, defaultColor: string) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.backgroundColor = defaultColor
  }
}

const handleMouseEnterGradient = (e: MouseEvent, color: string) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.background = `linear-gradient(to right, ${color}0D, transparent, transparent)`
  }
}

const handleMouseLeaveGradient = (e: MouseEvent) => {
  const target = e.currentTarget as HTMLElement
  if (target) {
    target.style.background = 'transparent'
  }
}

const handleResize = () => {
  isMobile.value = checkIfMobile()
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    showAllTechs.value = true
  }
}

// Handler für pageshow Event
const handlePageShow = (event: PageTransitionEvent) => {
  if (event.persisted) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
}

onBeforeMount(async () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    
    // Füge Klasse zu html hinzu für Scrollbar-Styling (früh, damit CSS greift)
    document.documentElement.classList.add('bey-hub-page')
    
    // Setze Fallback-Farbe SOFORT, bevor die Navigation rendert
    // Dies verhindert das rote Flackern
    document.documentElement.style.setProperty('--fallback-project-accent', '#2c6ae8')
    document.documentElement.style.setProperty('--project-accent', '#2c6ae8')
    
    // Lade Projekt-Daten SOFORT, bevor die Komponente gerendert wird
    // Dies verhindert das Flackern der Farbe
    try {
      const project = await getPortfolioProjectByPageUrl('/projects/bey-hub')
      if (project) {
        projectData.value = project
        // Setze Accent-Farbe SOFORT, bevor Vue rendert
        if (project.accentColor) {
          setAccentColor(project.accentColor)
        } else {
          // Fallback: Setze Standard-Farbe sofort
          if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty('--project-accent', '#2c6ae8')
          }
        }
      } else {
        // Fallback: Setze Standard-Farbe sofort
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--project-accent', '#2c6ae8')
        }
      }
    } catch (error) {
      // Bei Fehler: Setze Fallback-Farbe
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--project-accent', '#2c6ae8')
      }
    }
  }
})

onMounted(async () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  
  // Lade Projekt-Daten vollständig (falls noch nicht in onBeforeMount geladen)
  if (!projectData.value) {
    await loadProjectData()
  } else {
    // Wenn bereits geladen, nur Logo aktualisieren
    if (projectData.value) {
      if (isDark.value && projectData.value.logoDark) {
        logoUrl.value = projectData.value.logoDark
      } else if (!isDark.value && projectData.value.logoLight) {
        logoUrl.value = projectData.value.logoLight
      } else {
        logoUrl.value = '/projects/bey-hub/logo.webp'
      }
      
      if (projectData.value.liveUrl) {
        liveUrl.value = projectData.value.liveUrl
      }
    }
  }
  
  // Prüfe ob mobil
  isMobile.value = checkIfMobile()
  if (!isMobile.value) {
    showAllTechs.value = true
  }
  
  // Listener für Resize hinzufügen
  window.addEventListener('resize', handleResize)
  window.addEventListener('pageshow', handlePageShow)
  
  initScrollReveal()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('pageshow', handlePageShow)
  // Stoppe Watch für Theme-Änderungen
  stopWatch()
  // Entferne Klasse von html
  document.documentElement.classList.remove('bey-hub-page')
  // Entferne CSS-Variable
  if (typeof document !== 'undefined') {
    document.documentElement.style.removeProperty('--project-accent')
  }
})
</script>

<style scoped>
@keyframes blob-organic {
  0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
  33% { transform: translate(40px, -60px) scale(1.15) rotate(5deg); }
  66% { transform: translate(-30px, 30px) scale(0.95) rotate(-5deg); }
  100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
}

.animate-blob-organic {
  animation: blob-organic 10s infinite ease-in-out;
}

.animation-delay-1500 {
  animation-delay: 1.5s;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

/* Scroll Reveal */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.delay-100 { transition-delay: 0.1s; }
.delay-200 { transition-delay: 0.2s; }
.delay-300 { transition-delay: 0.3s; }
.delay-400 { transition-delay: 0.4s; }
.delay-500 { transition-delay: 0.5s; }

/* Fade Animation (Lightbox) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Mauszeiger und Scrollwheel in Featurefarbe für BEY-Hub Seite */
html.bey-hub-page :deep(.outer-ring) {
  border-color: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.3;
}

html.bey-hub-page :deep(.outer-ring.hover-active) {
  border-color: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.4;
}

html.bey-hub-page :deep(.custom-cursor) {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.6;
  box-shadow: 
    0 0 10px var(--bey-hub-accent, #2c6ae8),
    0 0 20px var(--bey-hub-accent, #2c6ae8),
    inset 0 0 10px var(--bey-hub-accent, #2c6ae8) !important;
}

html.bey-hub-page :deep(.custom-cursor.hover-active) {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.7;
  box-shadow: 
    0 0 15px var(--bey-hub-accent, #2c6ae8),
    0 0 30px var(--bey-hub-accent, #2c6ae8),
    0 0 45px var(--bey-hub-accent, #2c6ae8),
    inset 0 0 15px var(--bey-hub-accent, #2c6ae8) !important;
}

html.bey-hub-page :deep(.trail-dot) {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.5;
}
</style>

<style>
/* Scrollwheel in Featurefarbe für BEY-Hub Seite */
html.bey-hub-page,
html.bey-hub-page * {
  scrollbar-width: thin;
  scrollbar-color: var(--bey-hub-accent, #2c6ae8) rgba(0, 0, 0, 0.05) !important;
}

html.bey-hub-page ::-webkit-scrollbar,
html.bey-hub-page body ::-webkit-scrollbar {
  width: 12px !important;
  height: 12px !important;
}

html.bey-hub-page ::-webkit-scrollbar-track,
html.bey-hub-page body ::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05) !important;
  border-left: none !important;
  border-radius: 0 !important;
}

html.bey-hub-page ::-webkit-scrollbar-thumb,
html.bey-hub-page body ::-webkit-scrollbar-thumb {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.6;
  background-image: none !important;
  border-radius: 6px !important;
  border: 2px solid transparent !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.bey-hub-page ::-webkit-scrollbar-thumb:hover,
html.bey-hub-page body ::-webkit-scrollbar-thumb:hover {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.8;
  background-image: none !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.bey-hub-page ::-webkit-scrollbar-thumb:active,
html.bey-hub-page body ::-webkit-scrollbar-thumb:active {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.9;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Dark Mode Scrollwheel für BEY-Hub Seite */
html.bey-hub-page.dark,
html.bey-hub-page.dark * {
  scrollbar-color: var(--bey-hub-accent, #2c6ae8) rgba(255, 255, 255, 0.05) !important;
}

html.bey-hub-page.dark ::-webkit-scrollbar-track,
html.bey-hub-page.dark body ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05) !important;
  border-left: none !important;
}

html.bey-hub-page.dark ::-webkit-scrollbar-thumb,
html.bey-hub-page.dark body ::-webkit-scrollbar-thumb {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.7;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
}

html.bey-hub-page.dark ::-webkit-scrollbar-thumb:hover,
html.bey-hub-page.dark body ::-webkit-scrollbar-thumb:hover {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 0.9;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.bey-hub-page.dark ::-webkit-scrollbar-thumb:active,
html.bey-hub-page.dark body ::-webkit-scrollbar-thumb:active {
  background: var(--bey-hub-accent, #2c6ae8) !important;
  opacity: 1;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* IT Text Unterstreichung in BEY-Hub Farbe */
html.bey-hub-page .it-text::after {
  background-color: var(--bey-hub-accent, #2c6ae8) !important;
}

html.bey-hub-page.dark .it-text::after {
  background-color: var(--bey-hub-accent, #2c6ae8) !important;
}
</style>

